import { useCallback, useEffect, useRef } from 'react';
import { MonitorProps, patientCounters } from '../utils/patientCounters';
import { AlertConfig } from './alerts/alerts.types';
import { ALERT_REGISTRY } from '@/components/pages/kiosk/monitor/hooks/alerts/alert';

export interface AlertMonitoringProps extends MonitorProps {
  loading?: boolean;
  configuration?: AlertConfig;
}

// Audio pool for efficient sound management
const audioPool: HTMLAudioElement[] = [];
const MAX_AUDIO_POOL_SIZE = 20;

/**
 * Play sound alert with audio pooling for better performance
 * @param soundFile - Sound file path (string) or imported MP3 module (resolves to string URL)
 * @param condition - Optional condition to check before playing sound (defaults to true)
 */
export const playSound = async (soundFile: string | any, condition?: boolean): Promise<void> => {
  // If condition is explicitly false, don't play
  if (condition === false) return;

  try {
    // Ensure we have a valid sound file path
    if (!soundFile || typeof soundFile !== 'string') {
      console.warn('Sound alert error: Invalid sound file provided', { soundFile });
      return;
    }

    let sound: HTMLAudioElement | undefined;

    // Try to find an idle sound object in the pool
    for (const pooledSound of audioPool) {
      if (pooledSound.ended || pooledSound.paused) {
        sound = pooledSound;
        break;
      }
    }

    // If no idle sound found, create a new one (if under limit)
    if (!sound) {
      if (audioPool.length < MAX_AUDIO_POOL_SIZE) {
        sound = new Audio();
        audioPool.push(sound);
      } else {
        // Pool is full, reuse the oldest sound (interrupt it)
        sound = audioPool[0];
        sound.pause();
        sound.currentTime = 0;
      }
    }

    // Reset and play the sound
    sound.src = soundFile;
    await sound.play();
  } catch (e) {
    console.warn('Sound alert error:', { soundFile, error: e });
  }
};

/**
 * Unified alerts hook that handles both monitoring and sound playing
 * Replaces both useAlertMonitoring and useSoundAlerts
 */
export const useAlerts = (props: AlertMonitoringProps) => {
  const prevPropsRef = useRef<AlertMonitoringProps>(props);
  const { configuration } = props;

  const playAlert = useCallback(
    async (alertId: string) => {
      const alertDef = ALERT_REGISTRY.find(alert => alert.id === alertId);
      if (!alertDef) {
        console.warn(`Unknown alert: ${alertId}`);
        return;
      }

      // Check if this alert is enabled in configuration
      const isEnabled = !!configuration?.[alertDef.configFlag];
      if (isEnabled) {
        await playSound(alertDef.soundFile);
      }
    },
    [configuration]
  );

  useEffect(() => {
    const prevProps = prevPropsRef.current;

    // Only process alerts if not loading and props have changed
    if (!prevProps.loading && props !== prevProps) {
      // Process all alerts from the registry
      ALERT_REGISTRY.forEach(alertDef => {
        if (alertDef.condition(props, prevProps)) {
          playAlert(alertDef.id).then();
        }
      });
    }

    // Update previous props reference
    prevPropsRef.current = props;
  }, [props, playAlert]);

  return {
    // Expose counters for debugging or display purposes
    counters: {
      totalPatients: patientCounters.patientNumberFromProps(props),
      readyForOR: patientCounters.readyForORNumberFromProps(props),
      readyForSurgeon: patientCounters.readyForSurgeonNumberFromProps(props),
      holdProcedure: patientCounters.holdProcedureNumberFromProps(props),
      blockNerve: patientCounters.blockNerveNumberFromProps(props) + patientCounters.blockNerveNumberFromProps2(props),
      help: patientCounters.helpNumberFromProps(props),
      familyReady: patientCounters.familyHereNumberFromProps(props),
      readyToPickup: patientCounters.readyToPickupNumberFromProps(props),
      procedureEnd: patientCounters.procedureEndProps(props),
      closing: patientCounters.closingProps(props),
    },
    // Expose manual alert playing for testing or manual triggers
    playAlert,
  };
};
